import { get, map } from 'lodash';
import PropTypes from 'prop-types';
import React, { useCallback, useMemo, Fragment, useEffect } from 'react';
import { useMediaQuery } from 'react-responsive';

import BrandTenets from 'components/BrandTenets';
import MiniNavigation from 'components/MiniNavigation';
import { MINI_NAV_POSITIONS } from 'components/MiniNavigation/constants';
import PageSectionBodyCard from 'components/PageSectionBodyCard';
import PageSectionCardCarousel from 'components/PageSectionCardCarousel';
import PageSectionPropositionsCarousel from 'components/PageSectionPropositionsCarousel';
import PdpDifferentiators from 'components/PdpDifferentiators';
import PdpMasterProductDetails from 'components/PdpMasterProductDetails';
import PdpReviews from 'components/PdpReviews';
import PdpReviewsStarsJumplink from 'components/PdpReviewsStarsJumplink';
import PdpUgcCarousel from 'components/PdpUgcCarousel';
import { PAGE_SECTIONS, PAGE_SECTIONS_V3 } from 'constants/homepage';
import { mediumUp } from 'constants/mediaQueries';
import { ATTRIBUTE_BLOCK_TYPES, SIZE_SELECTOR_MODES } from 'constants/pdp';
import PdpAddToCartContainer from 'containers/PdpAddToCartContainer';
import PdpAttributesContainer from 'containers/PdpAttributesContainer';
import PdpBreadcrumbs from 'containers/PdpBreadcrumbsContainer';
import PdpCarouselContainer from 'containers/PdpCarouselContainer';
import PdpColorwayUpdatesFormContainer from 'containers/PdpColorwayUpdatesFormContainer';
import PdpEndOfPageRecsContainer from 'containers/PdpEndOfPageRecsContainer';
import PdpSwatchesContainer from 'containers/PdpProductSelectorContainer';
import PdpProductUpdatesFormContainer from 'containers/PdpProductUpdatesFormContainer';
import PdpSidebarRecsContainer from 'containers/PdpSidebarRecsContainer';
import PdpSizeChartContainer from 'containers/PdpSizeChartContainer';
import PdpSizeSelectorContainer from 'containers/PdpSizeSelectorContainer';
import PdpStoreInventoryContainer from 'containers/PdpStoreInventoryContainer';
import PdpStoryModuleContainer from 'containers/PdpStoryModuleContainer';
import PdpVideoModalContainer from 'containers/PdpVideoModalContainer';
import PdpZoomViewContainer from 'containers/PdpZoomViewContainer';
import UnisexPdpSelectorContainer from 'containers/UnisexPdpSelectorContainer';
import PageSectionBodyCardModel from 'models/PageSectionBodyCardModel';
import PageSectionCardCarouselModel from 'models/PageSectionCardCarousel';
import PageSectionPropositionsCarouselModel from 'models/PageSectionPropositionsCarousel';
import PageSectionStoryModuleModel from 'models/PageSectionStoryModule';
import PageSectionUgcCarouselModel from 'models/PageSectionUgcCarousel';
import Product from 'models/Product';
import Variant from 'models/Variant';
import Rudderstack from 'utils/Rudderstack';
import { cn } from 'utils/Tailwind';

const trackModuleEvent = ({
  cardName,
  cardPosition,
  moduleType,
  internalName,
  modulePosition,
  imageName,
  linkCTAName,
  linkDestination,
}) => {
  Rudderstack.pdpModuleClicked({
    cardName,
    cardPosition,
    moduleType,
    moduleName: internalName,
    modulePosition,
    imageName,
    linkCTAName,
    linkDestination,
  });
};

const Pdp = ({
  addToCartPolicyText,
  attributes,
  bulkAddOptions,
  currentVariant,
  descriptionSingleLine,
  differentiators,
  features,
  miniNavigationItems,
  pageSections,
  pdpStorytellingEnabled,
  pdpStorytellingModuleOrder,
  product,
  setCurrentAttributeIndex,
  sizeChart,
  sizingRecommendation,
}) => {
  const isMediumUp = useMediaQuery({ query: mediumUp });

  const basicProductProperties = useMemo(
    () => ({
      category: product?.type,
      fullName: product?.fullName,
      gender: product?.gender,
      masterName: product?.masterName,
    }),
    [product?.type, product?.fullName, product?.gender, product?.masterName]
  );

  const handleOpenSizing = useCallback(() => {
    const index = attributes.findIndex(attribute =>
      attribute.content?.content?.some(
        content =>
          content?.data?.target?.sys?.contentType?.sys?.id === ATTRIBUTE_BLOCK_TYPES.SIZE_CHART
      )
    );

    if (index !== -1) {
      const attributeElement = document.querySelector('[data-testid="attributes"]');

      if (attributeElement) {
        window.scrollTo({
          behavior: 'smooth',
          top:
            attributeElement.getBoundingClientRect().top -
            document.body.getBoundingClientRect().top -
            60,
        });
        setCurrentAttributeIndex(index, 'pdp', basicProductProperties);
      }
    }
  }, [attributes, basicProductProperties, setCurrentAttributeIndex]);

  const renderPageSection = useCallback(
    ({ data, index, key }) => {
      const pageSectionProps = {
        key,
        trackCTAClick: ({ url, text }) =>
          trackModuleEvent({
            moduleName: key,
            modulePosition: index,
            moduleType: data.type,
            text,
            url,
          }),
        ...get(data, '_data', {}),
      };

      switch (data.type) {
        case PAGE_SECTIONS.BODY_CARD:
          return <PageSectionBodyCard data={data} dataTestId="body-card" />;

        case PAGE_SECTIONS_V3.CARD_CAROUSEL:
          return pdpStorytellingEnabled ? (
            <PageSectionCardCarousel
              pdpStorytellingEnabled
              dataTestId="card-carousel"
              {...pageSectionProps}
            />
          ) : null;
        case PAGE_SECTIONS_V3.PROPOSITIONS_CAROUSEL:
          return pdpStorytellingEnabled ? (
            <PageSectionPropositionsCarousel
              dataTestId="propositions-carousel"
              {...pageSectionProps}
            />
          ) : null;
        case PAGE_SECTIONS.STORY_MODULE:
          return (
            <PdpStoryModuleContainer
              dataTestId={`story-module-${index}`}
              className={cn('', {
                'md:px-6 lg:mx-auto lg:max-w-screen-2xl': !pdpStorytellingEnabled,
                'md:px-6 lg:px-10': pdpStorytellingEnabled,
              })}
              {...pageSectionProps}
              data={data}
              key={data.internalName}
              modulePosition={index + 1}
            />
          );
        case PAGE_SECTIONS.UGC_CAROUSEL:
          return (
            <PdpUgcCarousel
              className="mt-10"
              data={data}
              pdpStorytellingEnabled={pdpStorytellingEnabled}
              dataTestId="ugc-carousel"
            />
          );
        /* istanbul ignore next */
        default:
          return null;
      }
    },
    [pdpStorytellingEnabled]
  );

  const _pageSections = map(pageSections, data => {
    switch (get(data, '_contentType')) {
      case PAGE_SECTIONS_V3.PROPOSITIONS_CAROUSEL:
        return new PageSectionPropositionsCarouselModel(data);
      case PAGE_SECTIONS_V3.CARD_CAROUSEL:
        return new PageSectionCardCarouselModel(data);
      case PAGE_SECTIONS.UGC_CAROUSEL:
        return new PageSectionUgcCarouselModel(data);
      case PAGE_SECTIONS.STORY_MODULE:
        return new PageSectionStoryModuleModel(data);
      case PAGE_SECTIONS.BODY_CARD:
        return new PageSectionBodyCardModel(data);
      default:
        return null;
    }
  }).filter(Boolean);

  const hasSizeChartAttribute = useMemo(() => {
    if (!attributes) return false;

    return attributes.some(attribute =>
      attribute.content?.content?.some(
        content =>
          content?.data?.target?.sys?.contentType?.sys?.id === ATTRIBUTE_BLOCK_TYPES.SIZE_CHART
      )
    );
  }, [attributes]);

  useEffect(() => {
    window.yotpoWidgetsContainer?.initWidgets?.();
  }, []);

  if (!product) {
    return null;
  }

  return (
    <div
      className={cn('Pdp my-4 md:mt-12', {
        'mt-8': miniNavigationItems.length > 0,
        'pdp-storytelling': pdpStorytellingEnabled,
      })}
    >
      {miniNavigationItems.length > 0 && !isMediumUp && (
        <MiniNavigation
          className="mb-2 bg-grey px-2"
          links={miniNavigationItems}
          position={MINI_NAV_POSITIONS.TOP}
        />
      )}

      {/* ----------- Above the fold ----------- */}

      <div
        className={cn('PdpATF lg:grid lg:grid-cols-12', {
          'mx-auto w-full max-w-screen-2xl px-6': !pdpStorytellingEnabled,
          'px-6 lg:gap-14 lg:px-10': pdpStorytellingEnabled,
        })}
      >
        {/* ----------- Carousel ----------- */}

        <div
          className={cn('col-span-7 hidden lg:block', {
            'top-24 self-start md:sticky': pdpStorytellingEnabled,
          })}
        >
          <PdpCarouselContainer product={product} />

          {!pdpStorytellingEnabled &&
            features.showDifferentiators &&
            differentiators?.cards?.length && (
              <PdpDifferentiators
                className={cn({
                  'my-10 max-lg:hidden': !pdpStorytellingEnabled,
                  hidden: pdpStorytellingEnabled,
                })}
                heading={differentiators.heading}
                cards={differentiators.cards}
              />
            )}

          {!pdpStorytellingEnabled && features.showAttributes && (
            <PdpAttributesContainer
              className={cn({
                'mt-10 max-lg:hidden': !pdpStorytellingEnabled,
                hidden: pdpStorytellingEnabled,
              })}
              basicProductProperties={basicProductProperties}
              attributes={attributes}
              sizeChart={sizeChart}
              pdpStorytellingEnabled={pdpStorytellingEnabled}
            />
          )}
        </div>

        {/* ----------- SIDEBAR ----------- */}

        <aside
          className={cn('relative max-lg:mb-10 lg:col-span-5', {
            'lg:pl-[72px]': !pdpStorytellingEnabled,
            '': pdpStorytellingEnabled,
          })}
        >
          <PdpProductUpdatesFormContainer />
          <PdpColorwayUpdatesFormContainer />

          <div className="Pdp__master-product-details-wrapper mb-6">
            <div className="Pdp__breadcrumbs">
              <PdpBreadcrumbs />
            </div>

            <PdpMasterProductDetails
              product={product}
              currentVariant={currentVariant}
              features={features}
              pdpStorytellingEnabled={pdpStorytellingEnabled}
              descriptionSingleLine={descriptionSingleLine}
            />

            <div className="Pdp__reviews-and-free-shipping mt-1.5 flex items-center">
              {features.pageHasReviewsSection && features.yotpoEnabled && (
                <PdpReviewsStarsJumplink />
              )}
            </div>
          </div>

          <div className="-mx-6 mb-6 lg:hidden">
            <PdpCarouselContainer product={product} />
          </div>

          {features.showUnisexButton && (
            <div className="Pdp__unisex-selector-wrapper -mt-4 mb-6 border-b border-solid border-black/20 lg:mb-6">
              <UnisexPdpSelectorContainer />
            </div>
          )}

          {features.showSwatchSelector && <PdpSwatchesContainer />}

          {features.showSizeSelector !== SIZE_SELECTOR_MODES.HIDDEN && (
            <PdpSizeSelectorContainer
              useDropdown={features.showSizeSelector === SIZE_SELECTOR_MODES.DROPDOWN}
              handleOpenSizing={handleOpenSizing}
              pdpStorytellingEnabled={pdpStorytellingEnabled}
            />
          )}

          {sizeChart && (
            <PdpSizeChartContainer
              basicProductProperties={basicProductProperties}
              handle={product.masterId}
              handleOpenSizing={handleOpenSizing}
              hasSizeChartAttribute={hasSizeChartAttribute}
              pdpStorytellingEnabled={pdpStorytellingEnabled}
              sizeChart={sizeChart}
              sizingRecommendation={sizingRecommendation}
            />
          )}

          <PdpAddToCartContainer
            bulkAddOptions={bulkAddOptions}
            features={features}
            policyText={addToCartPolicyText}
          />

          {features.showDifferentiators &&
            !pdpStorytellingEnabled &&
            differentiators?.cards?.length && (
              <div className="Pdp__differentiators-wrapper my-10 lg:hidden">
                <PdpDifferentiators
                  heading={differentiators.heading}
                  cards={differentiators.cards}
                />
              </div>
            )}

          {features.showPdpStoreInventory && <PdpStoreInventoryContainer />}

          {features.showAttributes && !pdpStorytellingEnabled && (
            <PdpAttributesContainer
              className={cn('my-10 lg:hidden', {
                'mt-0': features.showPdpStoreInventory,
              })}
              basicProductProperties={basicProductProperties}
              attributes={attributes}
              sizeChart={sizeChart}
              pdpStorytellingEnabled={pdpStorytellingEnabled}
            />
          )}

          <PdpSidebarRecsContainer className="mt-10" />
        </aside>
      </div>

      {/* ----------- Below the fold ----------- */}

      <div
        className={cn('PageSections flex flex-col gap-6 overflow-x-hidden pt-6', {
          'lg:gap-20 lg:pt-20': pdpStorytellingEnabled,
          'lg:gap-10 lg:pt-10': !pdpStorytellingEnabled,
        })}
      >
        {/* Container Image injected from DY - Desktop PDP Product Features Module */}
        {!pdpStorytellingEnabled && (
          <div
            data-testid="desktop-product-features-module"
            className="mx-6 max-w-screen-2xl max-lg:hidden lg:mx-auto lg:px-6"
          />
        )}

        {pdpStorytellingEnabled && features.showAttributes && (
          <PdpAttributesContainer
            attributes={attributes}
            basicProductProperties={basicProductProperties}
            className={cn({
              'px-6 lg:px-10': pdpStorytellingEnabled,
            })}
            dataTestId="attributes"
            sizeChart={sizeChart}
            pdpStorytellingEnabled={pdpStorytellingEnabled}
          />
        )}

        {map(_pageSections, (pageSectionData, index) => (
          <Fragment key={pageSectionData.internalName}>
            {renderPageSection({
              data: pageSectionData,
              index,
              key: pageSectionData.internalName,
            })}
          </Fragment>
        ))}

        {pdpStorytellingEnabled && <BrandTenets dataTestId="brand-tenets" />}

        {features.yotpoEnabled && features.pageHasReviewsSection && (
          <PdpReviews className="mt-10" dataTestId="reviews" masterName={product.masterName} />
        )}

        <PdpEndOfPageRecsContainer
          id="pdp-end-of-page-recs-container"
          dataTestId="pdp-recs-carousel"
          pdpStorytellingEnabled={pdpStorytellingEnabled}
        />

        <PdpZoomViewContainer product={product} />
        <PdpVideoModalContainer />
      </div>

      {/* Order modules based on DY test data */}
      <style>
        {pdpStorytellingModuleOrder?.map((module, index) => {
          return `[data-testid=${module}] { order: ${index + 1} }`;
        })}
      </style>
    </div>
  );
};

Pdp.propTypes = {
  addToCartPolicyText: PropTypes.string,
  attributes: PropTypes.arrayOf(
    PropTypes.shape({
      header: PropTypes.string,
      attributes: PropTypes.array,
    })
  ),
  bulkAddOptions: PropTypes.arrayOf(
    PropTypes.shape({
      bulkAddAmount: PropTypes.number.isRequired,
      title: PropTypes.string.isRequired,
    })
  ),
  currentVariant: PropTypes.instanceOf(Variant),
  categoryCarousel: PropTypes.object,
  descriptionSingleLine: PropTypes.object,
  differentiators: PropTypes.shape({
    heading: PropTypes.string.isRequired,
    cards: PropTypes.arrayOf(
      PropTypes.shape({
        heading: PropTypes.string.isRequired,
      })
    ).isRequired,
  }),
  features: PropTypes.shape({
    pageHasReviewsSection: PropTypes.bool,
    pageHasUgcCarouselSection: PropTypes.bool,
    showSizeSelector: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
    showDifferentiators: PropTypes.bool,
    showSwatchSelector: PropTypes.bool,
    showAttributes: PropTypes.bool,
    showAfterpayAboveReviewsStars: PropTypes.bool,
    showUnisexButton: PropTypes.bool,
    showShoprunner: PropTypes.bool,
    showFreeShippingMessage: PropTypes.bool,
    showPdpStoreInventory: PropTypes.bool,
    showMobileProductFeaturesModule: PropTypes.bool,
    showNewBelowTheFoldStoryModule: PropTypes.bool,
    yotpoEnabled: PropTypes.bool,
  }).isRequired,
  miniNavigationItems: PropTypes.arrayOf(PropTypes.string),
  pageSections: PropTypes.array,
  pdpStorytellingEnabled: PropTypes.bool,
  pdpStorytellingModuleOrder: PropTypes.array,
  product: PropTypes.instanceOf(Product).isRequired,
  productFeaturesModuleAssetUrl: PropTypes.string,
  setCurrentAttributeIndex: PropTypes.func,
  sizeChart: PropTypes.shape({
    title: PropTypes.string,
    supportingCopy1: PropTypes.shape({
      data: PropTypes.object,
      content: PropTypes.arrayOf(
        PropTypes.shape({
          data: PropTypes.object,
          content: PropTypes.arrayOf(
            PropTypes.shape({
              data: PropTypes.object,
              marks: PropTypes.array,
              value: PropTypes.string,
              nodeType: PropTypes.string,
            })
          ),
          nodeType: PropTypes.string,
        })
      ),
      nodeType: PropTypes.string,
    }),
    supportingCopy2: PropTypes.shape({
      data: PropTypes.object,
      content: PropTypes.arrayOf(
        PropTypes.shape({
          data: PropTypes.object,
          content: PropTypes.arrayOf(
            PropTypes.shape({
              data: PropTypes.object,
              marks: PropTypes.array,
              value: PropTypes.string,
              nodeType: PropTypes.string,
            })
          ),
          nodeType: PropTypes.string,
        })
      ),
      nodeType: PropTypes.string,
    }),
    sizeChartTables: PropTypes.arrayOf(
      PropTypes.shape({
        _contentType: PropTypes.string,
        internalName: PropTypes.string,
        heading: PropTypes.string,
        data: PropTypes.arrayOf(
          PropTypes.arrayOf(
            PropTypes.shape({
              value: PropTypes.string,
            })
          )
        ),
      })
    ),
  }),
  sizingRecommendation: PropTypes.string,
  trackModuleEvent: PropTypes.func,
  ugcCarousel: PropTypes.object,
};

Pdp.defaultProps = {
  addToCartPolicyText: '',
  attributes: [],
  bulkAddOptions: [],
  currentVariant: null,
  differentiators: null,
  isMiniNavigationExperimentLoading: false,
  miniNavigationExperimentAnalyticsData: null,
  miniNavigationItems: [],
  pdpStorytellingModuleOrder: [],
  productFeaturesModuleAssetUrl: '',
  showPdpHeroGallery: null,
  sizeChart: null,
  sizingRecommendation: '',
};

export default Pdp;
